import {
  FAILED_STATUS,
  JITTER_MAX_MS,
  MAX_PAGES,
  MODES,
  PAGE_LIMIT,
  WARN_EVERY,
  WINDOW_MS,
} from './config';
import { evaluateTask, shortId } from './engine';
import { parseServerDate } from './time';
import { appendLog, readAttempts, readMode, updateAttempt, writeAttempts } from './storage';
import { autoContinueApi } from './api';

const RECORD_TTL_MS = 24 * 60 * 60 * 1000;
const TOO_OLD = '6 soatdan eski';

// Shu tab ichida bir vaqtda ikkita tick yoki bitta task uchun ikkita so'rov bo'lmasligi uchun
let running = false;
const inFlight = new Set();

const defaultSleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const taskLabel = (task) =>
  `video ${shortId(task.task_id)}${task.lang ? ` (${String(task.lang).toUpperCase()})` : ''}`;

const errorSnippet = (text, max = 140) => (text ? String(text).slice(0, max) : '');

const requestErrorText = (error) => {
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;
  const message = typeof detail === 'string' ? detail : error?.message;
  return [status, message].filter(Boolean).join(' · ') || "noma'lum xato";
};

// Oxirgi 6 soatdagi failed tasklar. Tartib hujjatlashtirilmagan, shuning uchun sahifada
// oynaga tushadigan birorta ham task qolmaguncha (ko'pi bilan MAX_PAGES × PAGE_LIMIT) o'qiladi.
export async function listRecentFailed(api, nowMs) {
  const tasks = [];
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const data = await api.listFailed({ skip: page * PAGE_LIMIT, limit: PAGE_LIMIT });
    const result = Array.isArray(data?.result) ? data.result : [];
    tasks.push(...result);

    const total = Number(data?.total_count) || 0;
    const anyRecent = result.some((task) => {
      const failedAt = parseServerDate(task.failed_at);
      return failedAt === null || nowMs - failedAt <= WINDOW_MS;
    });
    if (result.length < PAGE_LIMIT || (page + 1) * PAGE_LIMIT >= total || !anyRecent) break;
  }
  return tasks.filter((task) => task && task.status === FAILED_STATUS);
}

function pruneAttempts(nowMs) {
  const attempts = readAttempts();
  let changed = false;
  Object.keys(attempts).forEach((id) => {
    const record = attempts[id] || {};
    const last = Math.max(record.lastAt || 0, record.seenAt || 0, record.dryAt || 0);
    if (nowMs - last > RECORD_TTL_MS) {
      delete attempts[id];
      changed = true;
    }
  });
  if (changed) writeAttempts(attempts);
}

function logSkipOnce(task, reason, mode, nowMs) {
  const record = readAttempts()[task.task_id];
  if (record?.skip === reason) return;
  updateAttempt(task.task_id, { skip: reason, seenAt: nowMs });
  appendLog({
    at: nowMs,
    taskId: task.task_id,
    mode,
    action: 'skip',
    reason,
    error: errorSnippet(task.error),
  });
}

// Bitta tekshiruv sikli. Qulf (lock.js) tashqarida olinadi.
export async function runTick({
  api = autoContinueApi,
  now = Date.now,
  sleep = defaultSleep,
  random = Math.random,
  notify = () => {},
  getMode = readMode,
} = {}) {
  if (getMode() === MODES.OFF) return { ran: false, reason: 'off' };
  if (running) return { ran: false, reason: 'busy' };
  running = true;
  const summary = { ran: true, sent: 0, dry: 0, skipped: 0 };

  try {
    pruneAttempts(now());
    const tasks = await listRecentFailed(api, now());

    const due = [];
    tasks.forEach((task) => {
      const result = evaluateTask(task, readAttempts()[task.task_id], now());
      if (result.state === 'skip' && result.reason !== TOO_OLD) {
        logSkipOnce(task, result.reason, getMode(), now());
        summary.skipped += 1;
      } else if (result.state === 'due') {
        due.push(task);
      }
    });
    if (!due.length) return summary;

    // Bir nechta admin brauzeri bir vaqtda urinmasligi uchun 0–20 s tasodifiy kutish
    await sleep(Math.floor(random() * JITTER_MAX_MS));

    for (const task of due) {
      const id = task.task_id;
      if (inFlight.has(id)) continue;
      inFlight.add(id);
      try {
        const mode = getMode();
        if (mode === MODES.OFF) break;

        // Boshqa tab yoki oldingi qadam yozuvni yangilagan bo'lishi mumkin — qayta baholash
        const record = readAttempts()[id];
        const check = evaluateTask(task, record, now());
        if (check.state !== 'due') continue;
        const attemptNo = check.count + 1;

        if (mode === MODES.DRY && record?.dryFor === attemptNo) continue;

        // Joriy holatni serverdan qayta tekshirish (boshqa admin allaqachon davom ettirgan bo'lishi mumkin)
        let status;
        try {
          status = await api.getStatus(id);
        } catch (error) {
          continue; // keyingi tickda yana urinadi, urinish hisoblanmaydi
        }
        if (status?.status !== FAILED_STATUS) {
          logSkipOnce(task, `holat: ${status?.status || "noma'lum"}`, mode, now());
          summary.skipped += 1;
          continue;
        }

        if (getMode() === MODES.DRY) {
          updateAttempt(id, { dryFor: attemptNo, dryAt: now(), seenAt: now() });
          appendLog({ at: now(), taskId: id, mode: MODES.DRY, action: 'dry', reason: `${attemptNo}-urinish`, error: errorSnippet(task.error) });
          notify('info', `Sinov: «${taskLabel(task)}» davom ettirilardi (${attemptNo}-urinish)`);
          summary.dry += 1;
          continue;
        }
        if (getMode() !== MODES.ON) break;

        // Urinish so'rov yuborilishidan oldin yoziladi: javob kelmasa ham takror yuborilmaydi
        updateAttempt(id, { count: attemptNo, lastAt: now(), seenAt: now() });
        try {
          await api.postContinue(id);
          appendLog({ at: now(), taskId: id, mode: MODES.ON, action: 'continue', reason: `${attemptNo}-urinish`, error: errorSnippet(task.error) });
          notify('success', `«${taskLabel(task)}» avtomatik davom ettirildi (${attemptNo}-urinish)`);
          summary.sent += 1;
        } catch (error) {
          appendLog({ at: now(), taskId: id, mode: MODES.ON, action: 'error', reason: `${attemptNo}-urinish`, error: errorSnippet(requestErrorText(error)) });
          notify('error', `«${taskLabel(task)}»: avto-davom xatosi (${attemptNo}-urinish)`);
        }

        // Cheklov yo'q, lekin har WARN_EVERY urinishda bir marta ogohlantiriladi (to'xtatilmaydi)
        if (attemptNo % WARN_EVERY === 0) {
          const reason = errorSnippet(task.error, 80) || "sabab ko'rsatilmagan";
          appendLog({ at: now(), taskId: id, mode: MODES.ON, action: 'warn', reason: `${attemptNo} marta urinildi — xato sababini tekshiring`, error: errorSnippet(task.error) });
          notify('warning', `«${taskLabel(task)}» ${attemptNo} marta urinildi — xato sababini tekshiring: ${reason}`);
        }
      } finally {
        inFlight.delete(id);
      }
    }
    return summary;
  } finally {
    running = false;
  }
}
